import React, { useEffect, useId, useState } from "react";
import { TForm, TFormProps } from "@/components/form/types";
import useUpdateEffect from "@/hooks/UpdateEffect";
import { useStateContext } from "@/components/state";
import { IS_SERVER } from "@/helpers/CompileHelper";

export const resolvePath = (path?: string, object?: object) => path
    // eslint-disable-next-line
    ?.split(/[\.\[\]\'\"]/)
    ?.filter(p => p)
    ?.reduce((o: any, p: string) => o ? o[p] : null, object);

export const setPath = <T>(object: object, path: string, value: T) => path
    // .split(".")
    .replaceAll("]", "")
    .split(/[.[]/)
    .reduce((o: any, p: string, i: number) => o[p] = path.split(/[.[]/).length === ++i ? value : o[p] || {}, object);

const useForm = <T>({
    ...props
}: TFormProps<T>): TForm<T> => {
    const id = useId();
    const key = props?.key ?? props?.name;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const context = props?.context ?? useStateContext();

    const [value, setValue] = useState(context && key ?
        resolvePath(key, context.state) ?? props.default : props.default
    );

    useEffect(() => {
        validate(value);
    }, [])

    // useUpdateEffect(() => {
    //     if (!context || !key) return;
    //     const _value = resolvePath(key, context?.state);
    //
    //     if (_value === value) return;
    //     setValue(_value ?? props.default);
    // }, [resolvePath(key, context?.state)])

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let _value = props?.type === "checkbox" ? e.target.checked as T | T[] : e.target.value as T | T[];
        if (props?.multiple) {
            const set = Array.isArray(value) ? new Set(value) : new Set([]);
            set.has(_value) ? set.delete(_value) : set.add(_value);
            _value = Array.from(set);
        }

        update(_value);
    }

    const update = (value: T | T[]) => {
        if (context && key) {
            const payload = {};
            setPath(payload, key, value);
            context.dispatch(payload);
        }

        validate(value);
        setValue(value); // TODO - look at this if experiencing issues
    }

    const validate = (v: T | T[]) => {
        if (IS_SERVER) return;
        if (!props?.onValidate) return;

        const element = document.getElementById(`${props.id}-${id}`);
        const _value = v != null ? v : value;

        const response = props?.onValidate?.(element as HTMLInputElement, _value);
        if (response === true) (element as HTMLInputElement)?.setCustomValidity?.("");
        if (response === true || response === false) return response;

        (element as HTMLInputElement)?.setCustomValidity?.("");
        return true;
    }

    const isChecked = () => {
        if (props?.type === "checkbox") {
            return value ?? false;
        }

        return undefined;
    }

    const inputType = () => {
        if (props?.type === "radio" && props?.multiple) {
            return "checkbox";
        }

        return props?.type;
    }

    const is = (v: T): boolean => {
        if (Array.isArray(value)) {
            return value.some(item => item === v);
        }

        return value === v;
    }

    const isNot = (v: T): boolean => {
        return !isBlank() && !is(v);
    }

    const isBlank = (): boolean => {
        if (["", null, undefined].includes(value)) return true;
        return false;
    }

    return {
        key,
        value,
        is,
        isNot,
        isBlank,
        update,
        props: {
            container: {
                className: props?.className ?? undefined,
                style: props?.style ?? undefined,
            },

            label: {
                id: `${props.id}-${id}`,
                label: props?.label,
                title: props?.title,
            },

            input: {
                id: `${props.id}-${id}`,
                value,

                name: props?.name,
                type: inputType(),
                title: props?.title,
                placeholder: props?.placeholder,
                pattern: props?.pattern,
                required: props?.required ?? true,
                disabled: props?.disabled,
                multiple: props?.multiple,
                checked: isChecked(),
                className: props?.container === "skip" ? (props?.className ?? "") : "",
                style: props?.container === "skip" ? props?.style ?? {} : {},

                onChange: props?.onChange ?? onChange,
            },

            options: {
                ...props?.options,
                includeBlank: props?.options?.includeBlank,
                data: props?.options?.data,
                label: {
                    force: props?.options?.label?.force,
                },
            },

            hidden: props?.hidden,
            tooltip: props?.tooltip,
        },
    }
};

export default useForm;
